<template>
  <b-container class="py-1">
    <b-card no-body>
      <b-card-header class="border-bottom-0">
        <slot name="header(top)" v-bind="tableContext" />

        <div class="d-flex align-items-center justify-content-between">
          <slot name="header(left)" v-bind="tableContext" />

          <div class="ml-auto">
            <slot name="header(right)" v-bind="tableContext" />
            <BaseDataTableSearchInput />
          </div>
        </div>

        <slot name="header(bottom)" v-bind="tableContext" />
      </b-card-header>

      <b-card-body>
        <BaseDataTableFilters />
        <BaseDataTableTable v-bind="$attrs" ref="tableRef">
          <!-- <template
            v-for="tableColumn in tableColumns"
            v-slot:[`head(${tableColumn.key})`]="slotProps"
          >
            <slot v-bind="slotProps" :name="`head(${tableColumn.key})`" />

            <template v-if="'actions' === tableColumn.key">
              <BaseCheckbox v-model="selectAll" :checked-value="true" />
            </template>
          </template> -->

          <template
            v-for="tableColumn in tableColumns"
            v-slot:[`search(${tableColumn.key})`]="data"
          >
            <slot
              v-bind="{ ...data, filters, addFilter }"
              :name="`search(${tableColumn.key})`"
            />
          </template>

          <template
            v-for="tableColumn in tableColumns"
            v-slot:[`cell(${tableColumn.key})`]="slotProps"
          >
            <slot v-bind="slotProps" :name="`cell(${tableColumn.key})`" />

            <template v-if="'actions' === tableColumn.key">
              <BaseCheckbox
                v-model="modelValue"
                :checked-value="slotProps.item.id"
              />
            </template>
          </template>
        </BaseDataTableTable>
      </b-card-body>

      <b-card-footer class="py-4">
        <div class="d-flex align-items-center justify-content-between">
          <BaseDataTablePageSizeSelect />
          <BaseDataTablePagination />
        </div>
      </b-card-footer>
    </b-card>
  </b-container>
</template>

<script>
import {
  defineComponent,
  computed,
  provide,
  toRefs,
  ref,
  watch,
  getCurrentInstance
} from '@vue/composition-api'

import { BaseCheckbox } from '@components/base'

import { BaseDataTableInjectionKey } from './BaseDataTable.constants'

import useBaseDataTableSearch from './useBaseDataTableSearch'
import useBaseDataTableFilters from './useBaseDataTableFilters'
import useBaseDataTablePagination from './useBaseDataTablePagination'
import useBaseDataTableSorting from './useBaseDataTableSorting'

import BaseDataTableTable from './BaseDataTableTable.vue'
import BaseDataTableFilters from './BaseDataTableFilters.vue'
import BaseDataTableSearchInput from './BaseDataTableSearchInput.vue'
import BaseDataTablePageSizeSelect from './BaseDataTablePageSizeSelect.vue'
import BaseDataTablePagination from './BaseDataTablePagination.vue'

export default defineComponent({
  name: 'BaseDataTableCashCall',
  inheritAttrs: false,
  components: {
    BaseCheckbox,

    BaseDataTableTable,
    BaseDataTableFilters,
    BaseDataTableSearchInput,
    BaseDataTablePageSizeSelect,
    BaseDataTablePagination
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    columns: {
      type: Array,
      default: () => []
    },
    items: {
      type: Function,
      required: true
    },
    total: {
      type: Number,
      default: 0
    },
    initialPageSize: {
      type: Number,
      default: 10
    },
    pageSizeOptions: {
      type: Array,
      default: () => [5, 10, 25, 50]
    },
    selectable: Boolean,
    tbodyTrClass: Function
  },
  emits: ['input'],
  setup(props, { emit }) {
    const instance = getCurrentInstance()
    const tableId = `t${instance.proxy._uid}`

    const { search, setSearch } = useBaseDataTableSearch()
    const { filters, addFilter, removeFilter } = useBaseDataTableFilters()
    const {
      currentPage,
      setCurrentPage,

      pageSize,
      setPageSize
    } = useBaseDataTablePagination(props.initialPageSize)
    const {
      sortBy,
      setSortBy,

      sortDesc,
      toggleSortDesc,

      sortDirection,
      toggleSortDirection
    } = useBaseDataTableSorting()

    const { items, total, pageSizeOptions, tbodyTrClass } = toRefs(props)

    const modelValue = computed({
      get: () => props.value,
      set: (newValue) => emit('input', newValue)
    })

    // Bulk selection
    const selectAll = ref(false)

    watch(selectAll, (newSelectAll) => {
      if (!props.selectable) return

      if (newSelectAll) {
        const { tableRef: bTable } = tableRef.value || {}

        if (!!bTable) {
          const newModelValue = bTable.localItems.map(
            (localItem) => localItem.id
          )

          emit('input', newModelValue)
        } else {
          emit('input', [])
        }
      } else {
        emit('input', [])
      }
    })

    watch(modelValue, (newModelValue) => {
      if (!newModelValue.length) selectAll.value = false
    })

    // Table
    const tableRef = ref(null)

    const tableContext = computed(() => ({
      search: search.value,
      filters: filters.value,
      currentPage: currentPage.value,
      pageSize: pageSize.value,
      sortBy: sortBy.value,
      sortDesc: sortDesc.value,
      sortDirection: sortDirection.value,
      total: total.value
    }))

    const tableColumns = computed(() => {
      if (!props.selectable) return props.columns

      const columns = props.columns.slice();

      // columns.splice(0, 0, {
      //   key: 'actions',
      //   label: '',
      //   sortable: false,
      //   searchable: false,
      //   stickyColumn: true
      // })
      return columns;
    })

    const handleRefresh = () => {
      if (!tableRef.value) return

      tableRef.value.$refs.tableRef.refresh()
    }

    provide(BaseDataTableInjectionKey, {
      tableId,
      columns: tableColumns,
      items,
      total,
      tbodyTrClass,

      search,
      setSearch,

      filters,
      addFilter,
      removeFilter,

      currentPage,
      setCurrentPage,

      pageSize,
      setPageSize,
      pageSizeOptions,

      sortBy,
      setSortBy,

      sortDesc,
      toggleSortDesc,

      sortDirection,
      toggleSortDirection
    })

    return {
      filters,
      addFilter,

      modelValue,

      selectAll,

      tableRef,
      tableContext,
      tableColumns,

      handleRefresh
    }
  }
})
</script>

<style>
>>> .table.b-table colgroup > col:first-child {
  width: 40px !important;
}
</style>
